import React, { useState } from "react";
import { useAuth } from "@/AuthContext";
import { Navigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) return toast.error("Enter username/email and password");
    setBusy(true);
    try {
      await login(identifier.trim(), password);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen tactical-grid flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <span className="font-heading font-bold text-emerald-400 text-2xl">W</span>
            </div>
            <div className="text-left">
              <div className="font-heading font-extrabold text-2xl leading-none">Watch Tower</div>
              <div className="label-caps mt-1">Trade Terminal</div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="card-tactical p-8 space-y-5" data-testid="login-form">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={16} className="text-emerald-400" />
              <span className="label-caps">Secure Sign-In</span>
            </div>
            <h1 className="font-heading font-bold text-2xl mt-1">Welcome back</h1>
            <p className="text-slate-400 text-sm mt-1">Enter your credentials to access the terminal.</p>
          </div>

          <div>
            <Label className="label-caps !text-slate-400">Username or Email</Label>
            <Input
              value={identifier}
              onChange={e => setIdentifier(e.target.value)}
              placeholder="admin"
              autoFocus
              data-testid="login-identifier-input"
              className="mt-1.5 bg-slate-900/70 border-slate-800 h-11"
            />
          </div>

          <div>
            <Label className="label-caps !text-slate-400">Password</Label>
            <div className="relative mt-1.5">
              <Input
                type={show ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="bg-slate-900/70 border-slate-800 h-11 pr-10"
              />
              <button
                type="button"
                onClick={() => setShow(!show)}
                data-testid="btn-toggle-password"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="btn-login-submit"
            className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold text-base"
          >
            {busy ? "Signing in..." : "Sign in"}
          </Button>

          <div className="text-xs text-slate-500 pt-2 border-t border-slate-800 text-center">
            Contact your administrator if you need access.
          </div>
        </form>
      </div>
    </div>
  );
}
